import React from 'react';
import { useAppSelector } from 'store/hooks';
import { Perk } from 'components/loyalty/models/PerkModel';
import { PerkTemplate } from 'components/perks/templates/models/PerkTemplate';
import { SplitButtonOption } from 'ui/SplitButton';

export const usePepperPerksTemplates = (toggleCreateModal: () => void) => {
    const { templates } = useAppSelector(state => state.perkTemplates);
    const hasTemplates = templates.length > 0;
    const [templatePickerOpen, setTemplatePickerOpen] = React.useState(false);
    const [choiceModalOpen, setChoiceModalOpen] = React.useState(false);
    const [selectedTemplatePerk, setSelectedTemplatePerk] = React.useState<Perk | null>(null);

    const handleStartFromScratch = React.useCallback(() => {
        setChoiceModalOpen(false);
        setSelectedTemplatePerk(null);
        toggleCreateModal();
    }, [toggleCreateModal]);

    const handleUseTemplate = React.useCallback(() => {
        setChoiceModalOpen(false);
        setTemplatePickerOpen(true);
    }, []);

    const handleEmptyStateCreateClick = React.useCallback(() => {
        if (hasTemplates) {
            setChoiceModalOpen(true);
        } else {
            handleStartFromScratch();
        }
    }, [hasTemplates, handleStartFromScratch]);

    const handleCloseChoiceModal = React.useCallback(() => {
        setChoiceModalOpen(false);
    }, []);

    const handleTemplateSelected = React.useCallback(
        (template: PerkTemplate) => {
            setTemplatePickerOpen(false);
            setSelectedTemplatePerk(template.perk as Perk);
            toggleCreateModal();
        },
        [toggleCreateModal]
    );

    const handleCloseTemplatePicker = React.useCallback(() => {
        setTemplatePickerOpen(false);
    }, []);

    const splitButtonOptions: SplitButtonOption[] = React.useMemo(
        () => [
            { id: 'from-scratch', label: 'Create from scratch', onClick: handleStartFromScratch },
            { id: 'from-template', label: 'Create from template', onClick: handleUseTemplate }
        ],
        [handleStartFromScratch, handleUseTemplate]
    );

    return {
        hasTemplates,
        templatePickerOpen,
        choiceModalOpen,
        selectedTemplatePerk,
        splitButtonOptions,
        handleStartFromScratch,
        handleUseTemplate,
        handleEmptyStateCreateClick,
        handleCloseChoiceModal,
        handleCloseTemplatePicker,
        handleTemplateSelected
    };
};
